import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Alert,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import GlobalIcon from "../../components/GlobalIcon/GlobalIcon";
import { BrandLoader } from "../../components/Brand";
import styles from "./BasketManageSection.module.css";

const SEARCH_PAGE_SIZE = 8;

function clientDisplayName(c) {
  return (
    c?.full_name ||
    c?.name ||
    c?.broker_client_code ||
    (c?.id != null ? `Client ${c.id}` : "—")
  );
}

function clientInitials(c) {
  const name = clientDisplayName(c);
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return String(name).slice(0, 2).toUpperCase() || "?";
}

function membersToDraft(members, allClients) {
  const list = Array.isArray(allClients) ? allClients : [];
  const byId = new Map(list.map((c) => [Number(c.id), c]));
  const byCode = new Map(
    list.map((c) => [String(c.broker_client_code ?? "").trim().toUpperCase(), c])
  );

  return (members || [])
    .map((m) => {
      const code = String(m.broker_client_code ?? "").trim();
      const codeKey = code.toUpperCase();
      const raw =
        (m.clientId != null && !Number.isNaN(Number(m.clientId))
          ? byId.get(Number(m.clientId))
          : null) || (codeKey ? byCode.get(codeKey) : null);

      let clientId = null;
      if (m.clientId != null && !Number.isNaN(Number(m.clientId))) {
        clientId = Number(m.clientId);
      } else if (raw?.id != null && !Number.isNaN(Number(raw.id))) {
        clientId = Number(raw.id);
      }

      if (!code && clientId == null) return null;

      return {
        rowKey: clientId != null ? `id-${clientId}` : `code-${codeKey || "unknown"}`,
        clientId,
        broker_client_code: code || raw?.broker_client_code || "—",
        full_name: m.full_name || raw?.full_name || raw?.name || "",
        multiplier: String(m.multiplier ?? 1),
      };
    })
    .filter(Boolean);
}

function draftFingerprint(draft) {
  return JSON.stringify(
    [...draft]
      .map((d) => ({
        id: d.clientId != null ? d.clientId : d.broker_client_code,
        m: String(d.multiplier),
      }))
      .sort((a, b) => String(a.id).localeCompare(String(b.id)))
  );
}

export default function BasketManageSection({
  basket,
  members,
  allClients,
  clientsLoading,
  isAdding,
  removingClientId,
  isDeletingBasket,
  onAddClients,
  onRemoveMember,
  onDeleteBasket,
}) {
  const [draft, setDraft] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [pickerCap, setPickerCap] = useState(SEARCH_PAGE_SIZE);
  const [deleteBasketOpen, setDeleteBasketOpen] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(membersToDraft(members, allClients));
    setPickerOpen(false);
    setPickerQuery("");
    setPickerCap(SEARCH_PAGE_SIZE);
    setLocalError(null);
  }, [basket?.id, members, allClients]);

  const draftIds = useMemo(
    () =>
      new Set(
        draft
          .map((d) => d.clientId)
          .filter((id) => id != null && !Number.isNaN(Number(id)))
          .map((id) => Number(id))
      ),
    [draft]
  );

  const draftCodes = useMemo(
    () =>
      new Set(
        draft
          .map((d) => String(d.broker_client_code ?? "").trim().toUpperCase())
          .filter(Boolean)
      ),
    [draft]
  );

  const baselineFingerprint = useMemo(
    () => draftFingerprint(membersToDraft(members, allClients)),
    [members, allClients]
  );
  const isDirty = draftFingerprint(draft) !== baselineFingerprint;

  const availableClients = useMemo(() => {
    const list = Array.isArray(allClients) ? allClients : [];
    return list.filter((c) => {
      if (c.id == null) return false;
      if (draftIds.has(Number(c.id))) return false;
      const code = String(c.broker_client_code ?? "").trim().toUpperCase();
      if (code && draftCodes.has(code)) return false;
      return true;
    });
  }, [allClients, draftIds, draftCodes]);

  const filteredAvailable = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return availableClients;
    return availableClients.filter((c) => {
      const code = String(c.broker_client_code ?? "").toLowerCase();
      const name = String(c.full_name || c.name || "").toLowerCase();
      const idStr = String(c.id ?? "");
      return code.includes(q) || name.includes(q) || idStr.includes(q);
    });
  }, [availableClients, pickerQuery]);

  const visibleSearchRows = useMemo(
    () => filteredAvailable.slice(0, pickerCap),
    [filteredAvailable, pickerCap]
  );

  useEffect(() => {
    setPickerCap(SEARCH_PAGE_SIZE);
  }, [pickerQuery]);

  const togglePicker = useCallback(() => {
    setLocalError(null);
    setPickerOpen((prev) => {
      if (prev) {
        setPickerQuery("");
        setPickerCap(SEARCH_PAGE_SIZE);
      }
      return !prev;
    });
  }, []);

  const handleAddFromSearch = useCallback((client) => {
    const id = Number(client.id);
    if (Number.isNaN(id)) return;
    setDraft((prev) => {
      if (prev.some((d) => d.clientId === id)) return prev;
      return [
        ...prev,
        {
          rowKey: `id-${id}`,
          clientId: id,
          broker_client_code: client.broker_client_code || "—",
          full_name: client.full_name || client.name || "",
          multiplier: "1",
        },
      ];
    });
    setLocalError(null);
  }, []);

  const handleMultiplierChange = useCallback((rowKey, value) => {
    setDraft((prev) =>
      prev.map((d) => (d.rowKey === rowKey ? { ...d, multiplier: value } : d))
    );
  }, []);

  const handleRemoveDraft = useCallback((rowKey) => {
    setDraft((prev) => prev.filter((d) => d.rowKey !== rowKey));
  }, []);

  const handleSave = useCallback(async () => {
    const client_data = [];
    for (const row of draft) {
      const clientId = Number(row.clientId);
      if (row.clientId == null || Number.isNaN(clientId)) {
        setLocalError(
          `Cannot save ${row.broker_client_code}: client id not found. Refresh clients and try again.`
        );
        return;
      }
      const n = Number(String(row.multiplier).trim());
      if (!Number.isFinite(n) || n < 0) {
        setLocalError("Multiplier must be a number greater than or equal to 0 for each client.");
        return;
      }
      client_data.push({ client_id: clientId, multiplier: n });
    }

    const originalIds = new Set(
      (members || []).map((m) => Number(m.clientId)).filter((n) => !Number.isNaN(n))
    );
    const draftIdSet = new Set(
      draft.map((d) => Number(d.clientId)).filter((n) => !Number.isNaN(n))
    );
    const toRemove = [...originalIds].filter((id) => !draftIdSet.has(id));

    setLocalError(null);
    setSaving(true);
    try {
      for (const id of toRemove) {
        await onRemoveMember(id);
      }
      if (client_data.length > 0) {
        await onAddClients(client_data);
      }
      setPickerOpen(false);
      setPickerQuery("");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        err?.message ||
        "Failed to save basket clients";
      setLocalError(msg);
    } finally {
      setSaving(false);
    }
  }, [draft, members, onAddClients, onRemoveMember]);

  const handleDeleteBasket = useCallback(async () => {
    setLocalError(null);
    try {
      await onDeleteBasket();
      setDeleteBasketOpen(false);
    } catch (err) {
      setLocalError(
        err?.response?.data?.detail || err?.message || "Failed to delete basket"
      );
    }
  }, [onDeleteBasket]);

  const busy =
    saving || isAdding || isDeletingBasket || removingClientId != null;

  return (
    <div className={styles.card}>
      {localError ? (
        <Alert severity="error" onClose={() => setLocalError(null)} sx={{ mb: 1.5 }}>
          {localError}
        </Alert>
      ) : null}

      <div className={styles.cardHeader}>
        <h3 className={styles.basketName}>{basket.basket_name}</h3>
        <div className={styles.cardHeaderActions}>
          <Button
            type="button"
            variant="outlined"
            className={styles.saveBtn}
            onClick={handleSave}
            startIcon={saving || isAdding ? <BrandLoader variant="icon" size={16} /> : null}
            disabled={busy || !isDirty}
          >
            {saving || isAdding ? "Saving…" : "SAVE"}
          </Button>
          <button
            type="button"
            className={styles.deleteIconBtn}
            aria-label="Delete basket"
            disabled={busy}
            onClick={() => {
              setLocalError(null);
              setDeleteBasketOpen(true);
            }}
          >
            <GlobalIcon iconName="DeleteTrashIcon" style={{ width: 18, height: 18 }} />
          </button>
        </div>
      </div>

      <div className={styles.clientsBar}>
        <span className={styles.clientsTitle}>
          Clients <span className={styles.clientsCount}>({draft.length})</span>
        </span>
        <Button
          type="button"
          className={styles.addClientBtn}
          onClick={togglePicker}
          disabled={busy || clientsLoading}
          startIcon={<GlobalIcon iconName="AddIcon" style={{ width: 16, height: 16 }} />}
        >
          Add Client
        </Button>
      </div>

      {pickerOpen ? (
        <div className={styles.pickerPanel}>
          <TextField
            className={styles.searchField}
            placeholder="Search client name, client code....."
            value={pickerQuery}
            onChange={(e) => setPickerQuery(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <GlobalIcon
                    iconName="SearchIcon"
                    style={{ width: 18, height: 18, opacity: 0.45 }}
                  />
                </InputAdornment>
              ),
            }}
          />

          {clientsLoading ? (
            <div className={styles.pickerEmpty}>
              <BrandLoader variant="row" caption="Loading clients…" />
            </div>
          ) : availableClients.length === 0 ? (
            <div className={styles.pickerEmpty}>No more clients available to add.</div>
          ) : filteredAvailable.length === 0 ? (
            <div className={styles.pickerEmpty}>No clients match your search.</div>
          ) : (
            <div className={styles.searchList}>
              {visibleSearchRows.map((c) => {
                const id = Number(c.id);
                return (
                  <div key={id} className={styles.searchRow}>
                    <span className={styles.avatar} aria-hidden>
                      {clientInitials(c)}
                    </span>
                    <div className={styles.searchRowBody}>
                      <span className={styles.searchRowName}>{clientDisplayName(c)}</span>
                      <span className={styles.searchRowMeta}>
                        {c.broker_client_code || id}
                      </span>
                    </div>
                    <button
                      type="button"
                      className={styles.searchAddLink}
                      onClick={() => handleAddFromSearch(c)}
                      disabled={busy}
                    >
                      Add
                    </button>
                  </div>
                );
              })}
              {filteredAvailable.length > pickerCap ? (
                <button
                  type="button"
                  className={styles.loadMoreBtn}
                  onClick={() => setPickerCap((n) => n + SEARCH_PAGE_SIZE)}
                >
                  Load more ({filteredAvailable.length - pickerCap} remaining)
                </button>
              ) : null}
            </div>
          )}
        </div>
      ) : null}

      {draft.length === 0 ? (
        <p className={styles.emptyMembers}>
          No clients in this basket yet. Click &ldquo;Add Client&rdquo; to add one.
        </p>
      ) : (
        <div className={styles.cardsSection}>
          <div className={styles.cardsHead}>
            <span>CLIENT</span>
            <span className={styles.multHead}>MULTIPLIER</span>
          </div>
          <div className={styles.cardsList}>
            {draft.map((row) => {
              const removing =
                removingClientId != null &&
                row.clientId != null &&
                String(removingClientId) === String(row.clientId);
              return (
                <div key={row.rowKey} className={styles.clientCard}>
                  <div className={styles.clientCell}>
                    <span className={styles.avatar} aria-hidden>
                      {clientInitials(row)}
                    </span>
                    <div className={styles.clientCellText}>
                      <span className={styles.searchRowName}>
                        {row.full_name || row.broker_client_code}
                      </span>
                      <span className={styles.searchRowMeta}>
                        {row.broker_client_code}
                        {row.clientId != null ? ` · ${row.clientId}` : ""}
                      </span>
                    </div>
                  </div>
                  <div className={styles.cardRight}>
                    <TextField
                      type="number"
                      size="small"
                      value={row.multiplier}
                      onChange={(e) => handleMultiplierChange(row.rowKey, e.target.value)}
                      disabled={busy}
                      inputProps={{
                        min: 0,
                        step: "any",
                        "aria-label": `Multiplier for ${row.broker_client_code}`,
                      }}
                      className={styles.multField}
                      variant="outlined"
                    />
                    <button
                      type="button"
                      className={styles.rowRemoveBtn}
                      aria-label={`Remove ${row.broker_client_code}`}
                      disabled={busy || removing}
                      onClick={() => handleRemoveDraft(row.rowKey)}
                    >
                      {removing ? (
                        <BrandLoader variant="icon" size={14} />
                      ) : (
                        <GlobalIcon
                          iconName="RoundedCrossIcon"
                          style={{ width: 14, height: 14 }}
                        />
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog
        open={deleteBasketOpen}
        onClose={isDeletingBasket ? undefined : () => setDeleteBasketOpen(false)}
      >
        <DialogTitle sx={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}>
          Delete this basket?
        </DialogTitle>
        <DialogContent>
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 14,
              color: "var(--text-default)",
              margin: 0,
            }}
          >
            This will permanently remove &ldquo;{basket.basket_name}&rdquo; and its client
            links. This cannot be undone.
          </p>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteBasketOpen(false)}
            disabled={isDeletingBasket}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteBasket}
            disabled={isDeletingBasket}
            startIcon={isDeletingBasket ? <BrandLoader variant="icon" size={16} /> : null}
            sx={{ textTransform: "none" }}
          >
            {isDeletingBasket ? "Deleting…" : "Delete basket"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
